// T-373 — Hook di stato del Menu Manager: legge lo snapshot del menù del
// business attivo e espone le azioni CRUD. Online-diretto (vedi menuApi).

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { MenuCategory, MenuItem, MenuCategoryInput, MenuItemInput } from '@trackeat/shared';
import { useAuth } from '../../../lib/auth/AuthProvider.js';
import type { MenuType } from './menuType.js';
import * as api from './menuApi.js';

export function menuQueryKey(businessId: string | null, menuType: MenuType = 'menu'): (string | null)[] {
  return ['menu-manager', businessId, menuType];
}

/** sort_order successivo (in coda) dato un elenco già ordinato. */
function nextOrder(rows: { sort_order: number }[]): number {
  return (rows[rows.length - 1]?.sort_order ?? 0) + 10;
}

export function useMenu(menuType: MenuType = 'menu') {
  const { profile } = useAuth();
  const businessId = profile?.active_business_id ?? null;
  const tenantId = profile?.active_tenant_id ?? null;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: menuQueryKey(businessId, menuType),
    queryFn: () => api.fetchMenu(businessId as string, menuType),
    enabled: !!businessId,
    staleTime: 30_000,
  });

  const snapshot = query.data ?? null;
  const reload = useCallback(() => {
    void qc.invalidateQueries({ queryKey: menuQueryKey(businessId, menuType) });
  }, [qc, businessId, menuType]);

  // ── Categorie ──────────────────────────────────────────────────────────────
  const addCategory = useCallback(
    async (input: MenuCategoryInput) => {
      if (!businessId || !tenantId) throw new Error('no_business');
      const cats = snapshot?.categories ?? [];
      await api.createCategory(businessId, tenantId, input, nextOrder(cats), menuType);
      reload();
    },
    [businessId, tenantId, snapshot, reload, menuType],
  );
  const editCategory = useCallback(
    async (cat: MenuCategory, patch: Partial<MenuCategory>) => {
      const updated = await api.updateCategory(cat.id, patch, cat.version, menuType);
      reload();
      return updated;
    },
    [reload, menuType],
  );
  const removeCategory = useCallback(
    async (id: string) => {
      await api.deleteCategory(id, menuType);
      reload();
    },
    [reload, menuType],
  );

  // ── Piatti ───────────────────────────────────────────────────────────────────
  const addItem = useCallback(
    async (input: MenuItemInput) => {
      if (!businessId || !tenantId) throw new Error('no_business');
      const inCat = (snapshot?.items ?? []).filter((i) => i.category_id === input.category_id);
      const created = await api.createItem(businessId, tenantId, input, nextOrder(inCat), menuType);
      reload();
      return created;
    },
    [businessId, tenantId, snapshot, reload, menuType],
  );
  const editItem = useCallback(
    async (item: MenuItem, patch: Partial<MenuItem>) => {
      const updated = await api.updateItem(item.id, patch, item.version, menuType);
      reload();
      return updated;
    },
    [reload, menuType],
  );
  const removeItem = useCallback(
    async (item: MenuItem, slug: string | null) => {
      await api.deleteItem(item.id, menuType);
      if (slug && item.photo_url) await api.removeItemPhoto(slug, item.id);
      reload();
    },
    [reload, menuType],
  );

  const reorderItems = useCallback(
    async (orderedIds: string[]) => {
      await api.reorderItems(orderedIds, menuType);
      reload();
    },
    [reload, menuType],
  );
  const reorderCategories = useCallback(
    async (orderedIds: string[]) => {
      await api.reorderCategories(orderedIds, menuType);
      reload();
    },
    [reload, menuType],
  );

  const setPublished = useCallback(
    async (next: boolean) => {
      if (!businessId) return;
      await api.setPublished(businessId, next);
      await qc.invalidateQueries({ queryKey: ['menu-manager', businessId] });
    },
    [businessId, qc],
  );

  return {
    businessId,
    tenantId,
    snapshot,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    reload,
    addCategory,
    editCategory,
    removeCategory,
    addItem,
    editItem,
    removeItem,
    reorderItems,
    reorderCategories,
    setPublished,
  };
}
